import React from "react";
import { Dropdown, DropdownContent, DropdownItem, DropdownOptionalItem, DropdownTrigger } from "@emisgroup/ui-dropdown";

export type DropdownValueType = string | number | undefined;

/**
 * The list item type used in Dropdowns
 */
export type IListItem = {
  /**
   * Sets the list item to disabled state
   */
  disabled?: boolean;
  /**
   * The display text of the item
   */
  text: string;
  /**
   * The value of the item
   */
  value: DropdownValueType;
};

interface DropdownProps {
  /**
   * Data test id for the current dropdown
   */
  dataTestId?: string;
  /**
   * The aria label
   */
  ariaLabel?: string;
  /**
   * The aria-labelledby reference
   */
  ariaLabelledBy?: string;
  /**
   * A data source used to fetch data the dropdown should display
   */
  dataSource: IListItem[];
  /**
   * Determines whether the dropdown is disabled or not
   */
  disabled?: boolean;
  /**
   * Determines whether the dropdown is valid or not
   */
  invalid?: boolean;
  /**
   * Name of the element
   */
  name?: string;
  /**
   * The onChange callback, returning both the value of the selected item and the event
   */
  onChange?: (selectedValue: string) => void;
  /**
   * Determines whether an optional (Blank) field is required or not
   */
  optional?: boolean;
  /**
   * The value from the data source to be selected by default
   */
  value?: DropdownValueType;
  /**
   * The id of the element
   */
  id?: string;
  /**
   * The class of the element
   */
  className?: string;
  /**
   * Default value of the dropdown
   */
  defaultValue?: string;
}

/**
 * Responsible for rendering the dropdown component
 * @returns dropdown element
 */
export const UIDropdown: React.FC<DropdownProps> = (props: DropdownProps) => {

  const {
    dataSource,
    name,
    id,
    dataTestId,
    onChange,
    value,
    optional,
    disabled,
    invalid,
    defaultValue
  } = props;

  return (
    <Dropdown
      data-testid={dataTestId}
      name={name}
      onValueChange={onChange}
      id={id}
      disabled={disabled}
      defaultValue={defaultValue}
      value={String(value)}
    >
      <DropdownTrigger invalid={invalid}/>
      {optional && <DropdownOptionalItem />}
      <DropdownContent
        id={`${id}-content`}
      >
        {dataSource.length &&
          dataSource?.map((dataItem: IListItem, idx: number) => {
            return (
              <DropdownItem
                key={idx}
                value={dataItem?.value ? String(dataItem?.value) : ""}
                disabled={dataItem?.disabled}
              >
                {dataItem?.text}
              </DropdownItem>
            );
          })
        }
      </DropdownContent>
    </Dropdown>
  );
};
